/**
 * Home. A single aggregated query drives the tiles so the screen settles at
 * once instead of popping in section by section.
 */
import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  CheckSquare,
  CalendarClock,
  Timer,
  Target,
  ArrowRight,
  ExternalLink,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useWorkMode } from "@/hooks/use-work-mode";
import { useNowMinute } from "@/components/dashplan/now-marker";
import { KINDS, type DashplanKind } from "@/components/dashplan/constants";
import { todayKey, formatMinute, formatDuration } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { RecentBookmarks } from "@/components/recent-bookmarks";

export default function DashboardPage() {
  const { workMode } = useWorkMode();
  const nowMinute = useNowMinute(true);
  const date = todayKey();

  const summary = useQuery(api.dashboard.summary, {
    date,
    nowMinute,
    workMode,
  });

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="page-title text-2xl font-semibold">
          {greeting()}
        </h1>
        <p className="text-sm text-muted-foreground font-mono tabular-nums">
          {date} · {formatMinute(nowMinute)}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          to="/dailies"
          icon={CheckSquare}
          label="Dailies"
          value={summary ? String(summary.tasks.pending) : "–"}
          detail={
            summary
              ? summary.tasks.overdue > 0
                ? `${summary.tasks.overdue} overdue`
                : summary.tasks.highPriority > 0
                  ? `${summary.tasks.highPriority} high priority`
                  : "nothing overdue"
              : undefined
          }
          tone={summary && summary.tasks.overdue > 0 ? "warn" : undefined}
        />
        <Tile
          to="/dashplan"
          icon={CalendarClock}
          label="Dashplan"
          value={
            summary ? `${summary.dashplan.done}/${summary.dashplan.total}` : "–"
          }
          detail={
            summary
              ? `${formatDuration(summary.dashplan.plannedMinutes)} planned`
              : undefined
          }
        />
        <Tile
          to="/focus"
          icon={Timer}
          label="Focus today"
          value={summary ? formatDuration(summary.focus.focusMinutes) : "–"}
          detail={
            summary
              ? `${summary.focus.periods} period${summary.focus.periods === 1 ? "" : "s"}`
              : undefined
          }
        />
        <Tile
          to="/quests"
          icon={Target}
          label="Open goals"
          value={summary ? String(summary.goals.open) : "–"}
          detail={
            summary && summary.goals.dueSoon > 0
              ? `${summary.goals.dueSoon} due within 2 weeks`
              : "on track"
          }
        />
      </div>

      {/* Right now / next up */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            {summary?.dashplan.current ? "Right now" : "Next up"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary === undefined ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : summary.dashplan.current ?? summary.dashplan.next ? (
            <CurrentBlock
              entry={(summary.dashplan.current ?? summary.dashplan.next)!}
            />
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {summary.dashplan.total === 0
                  ? "Nothing planned for today yet."
                  : "Everything for today is behind you."}
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to="/dashplan">
                  Plan the day
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <RecentBookmarks />
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return "Still up?";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function CurrentBlock({
  entry,
}: {
  entry: {
    title: string;
    kind: string;
    startMinute: number;
    durationMinutes: number;
    location?: string;
  };
}) {
  const kind = KINDS[entry.kind as DashplanKind] ?? KINDS.other;
  const Icon = kind.icon;

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border",
          kind.surface,
          kind.accent,
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{entry.title}</p>
        <p className="text-xs text-muted-foreground font-mono tabular-nums">
          {formatMinute(entry.startMinute)}–
          {formatMinute(
            Math.min(1439, entry.startMinute + entry.durationMinutes),
          )}
          {entry.location && <span className="font-sans"> · {entry.location}</span>}
        </p>
      </div>
      <Button asChild size="sm" variant="ghost">
        <Link to="/dashplan" aria-label="Open Dashplan">
          <ExternalLink className="w-4 h-4" />
        </Link>
      </Button>
    </div>
  );
}

function Tile({
  to,
  icon: Icon,
  label,
  value,
  detail,
  tone,
}: {
  to: string;
  icon: typeof CheckSquare;
  label: string;
  value: string;
  detail?: string;
  tone?: "warn";
}) {
  return (
    <Link
      to={to}
      className="rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p
        className={cn(
          "text-2xl font-semibold tabular-nums",
          tone === "warn" && "text-amber-400",
        )}
      >
        {value}
      </p>
      {detail && <p className="text-xs text-muted-foreground mt-1">{detail}</p>}
    </Link>
  );
}
